import React, { useState, useEffect, forwardRef, useRef } from "react";
import { TbClockHour2 } from "react-icons/tb";
import styled from "styled-components";
import ShareButtons from "../SocialMedia/ShareButtons";
import DynamicComponent from "../DynamicComponent";
import Image from "../Image";
import classNames from "classnames";
import IStyledProps from "../../utilities/IStyledProps";
import IEditableProps from "../../utilities/IEditableProps";
import {calculateDate} from "../../utilities/dateTime";
import { usePageProps } from '../../context/PagePropContext';
import BlogStyles from "./blogStyles";

const BlogContainer = styled.div`
    .blog-title {
        font-size: 1.778rem;
        font-family: "jubilat-variable", sans-serif;
        font-variation-settings: 'wght' 350;
        font-weight: 400;
    }
    .blog-title-small {
        font-size: 1.333rem;
        font-family: "jubilat-variable", sans-serif;
        font-variation-settings: 'wght' 350;
        font-weight: 400;
    }
    .blog-post-age {
        font-size: 0.889rem;
        margin-bottom: 0.611rem;
    }
    .share-article {
        font-size: 0.611rem;
        font-weight: 300;
    }
    .description {
        background: #E2E2E233;
        border-radius: 0.444rem 0rem 0rem 0rem;
        padding: 1.333rem 0.889rem 1.333rem 0.889rem;
        font-size: 1rem;

        p {
            margin-bottom: 0;
        }
    }
`;

const TagsContainer = styled.span`
    a {
        font-size: 0.611rem;
        font-weight: 300;
    }
    a:hover {
        text-decoration: none;
    }
`;

function Tags({ tags }) {
    return tags.map((tag, index) => (
        <TagsContainer className="pr-1 text-primary" key={tag}>
            <small>
                <a data-test-id="blog-tag-link" className="text-primary" href={`/must-reads?tag=${tag}`} rel="noopener noreferrer" target="_blank">
                    {tag}{index !== tags.length - 1 && ","}
                </a>
            </small>
        </TagsContainer>
    ));
};

function BlogLink({ href, children, ...props }) {
    if (href) {
        return (
            <a href={href} className="text-decoration-none" {...props}>
                {children}
            </a>
        );
    }

    return children;
}

export interface IBlogProps extends IStyledProps, IEditableProps {
    dateType?: string;
    dateFormat?: string;
    src?: string;
    alt?: string;
    description?: string;
    headerAs?: string;
    showSocialMedia?: boolean;
    headerClassName?: string;
    href?: string;
    title: string;
    timestamp: number | string;
    tags: string;
    testIdPrefix?: string;
    isMainArticle?: boolean;
}

const Blog = forwardRef((props: IBlogProps, ref: any) => {
    const { cmsId, editorClassObj, headerAs = "big", description, showSocialMedia, title, timestamp: blogPropsTimestamp, tags: tagsProp, src, alt, headerClassName, href, dateType = "date", dateFormat = "MMMM d, yyyy", testIdPrefix = '', isMainArticle=true } = props;
    const { timestamp: pagePropsTimestamp, updateTimestamp, displayUpdateTimestamp, tags, blogTitle, imageSrc, imageAlt } = usePageProps(); // these props come in separate from 'props' because they need to be in frontmatter for meta tags
    const displaytitle = title ?? blogTitle;
    const displayDate = blogPropsTimestamp ?? pagePropsTimestamp;
    const displayTags = tagsProp ?? tags;
    const displaySrc = src ?? imageSrc;
    const displayAlt = alt ?? imageAlt;
    const showMainBlogHeader = headerAs === 'big' || headerAs === 'h1';
    const headersMap = { big: 'h1', small: 'h5' };
    const headerTag = headersMap[headerAs] || headerAs;

    const [postAge, setPostAge] = useState(calculateDate(displayDate, dateType, dateFormat));
    const [postUpdateDate, setPostUpdateDate] = useState(calculateDate(updateTimestamp, dateType, dateFormat));
    const [url, setUrl] = useState(null);
    const [readTime, setReadTime] = useState(null);

    const dateRef = useRef(null);
    const mainRef = useRef(ref || null);

    useEffect(() => {
        if (window) {
            setUrl(window.location.href);
        }
    }, []);

    useEffect(() => {
        setPostAge(calculateDate(displayDate, dateType, dateFormat));
    }, [displayDate, dateType]);

    useEffect(() => {
        setPostUpdateDate(calculateDate(updateTimestamp, dateType, dateFormat));
    }, [displayUpdateTimestamp, updateTimestamp, dateType]);

    useEffect(() => {
        if (dateRef.current && mainRef.current) {
            const previousElement = mainRef.current.previousElementSibling;
            if (!previousElement || !(previousElement.classList.contains('yt-thumbnail-container') || previousElement.classList.contains('yt-video-container'))) {
                return;
            };

            const parentElement = previousElement.parentElement;
            if (parentElement && dateRef.current && !document.body.contains(dateRef.current)) {
                dateRef.current.setAttribute('style', 'margin-bottom: 0.611rem;');
                parentElement.insertBefore(dateRef.current, previousElement);
            }
        }

        return () => {
            if (dateRef.current && dateRef.current.parentElement) {
                dateRef.current.parentElement.removeChild(dateRef.current);
            }
        };
    }, [url, dateRef, mainRef]);

    useEffect(() => { // Calculate read time
        let element = document.querySelector('.custom-content.markdown-container');

        if (!element) {
            element = document.querySelector('.custom-content.markup-container');
        }

        if (element) {
            const readWordsMin = 175;
            const readWordsMax = 250;
            const blogWordCount = element.textContent.trim().split(/\s+/).length;
            const readTimeMin = Math.round(blogWordCount / readWordsMax);
            const readTimeMax = Math.round(blogWordCount / readWordsMin);

            setReadTime(readTimeMin === readTimeMax ? readTimeMin.toString() : `${readTimeMin}-${readTimeMax}`);
        }
    }, [url]);

    return (
        <div ref={mainRef} data-id={cmsId} className={classNames(editorClassObj, 'mb-4')}>
            {showMainBlogHeader && <BlogStyles />}
            <BlogContainer>
                {isMainArticle &&
                <div ref={dateRef} className="w-100 pt-3 d-inline-block text-muted">
                    <div data-test-id="blog-date" className="float-left blog-post-age">
                        <strong>Published:</strong> {dateType === 'timeAgo' ? postAge.toUpperCase() : postAge}{postUpdateDate && displayUpdateTimestamp && <> | Last updated {dateType === 'timeAgo' ? postUpdateDate.toUpperCase() : postUpdateDate}</>}
                    </div>
                </div>}
                <BlogLink data-test-id={`${testIdPrefix}blog-title-link`} href={href}>
                    {displaySrc && <Image dataTestId="blog-image" src={displaySrc} alt={displayAlt} />}
                    <DynamicComponent data-test-id={`${testIdPrefix}blog-title`} as={headerTag} className={classNames(headerClassName, { "blog-title": showMainBlogHeader, "blog-title-small": !showMainBlogHeader }, "text-primary text-serif mt-4 mb-0")}>
                        {displaytitle}
                    </DynamicComponent>
                </BlogLink>
                <div className="w-100 pt-3 d-inline-block text-muted">
                    {!isMainArticle &&
                    <div data-test-id="blog-date" className="float-left blog-post-age">
                        {dateType === 'timeAgo' ? postAge.toUpperCase() : postAge}
                    </div>}
                    {isMainArticle && readTime && <div className="text-muted float-left">
                        <TbClockHour2 size={24} /> {readTime} minute read
                    </div>}
                    {showSocialMedia && <div className="float-right ml-2 d-none d-sm-block blog-share-tags">
                        <span className="share-article text-primary mr-3 align-middle">
                            SHARE THIS ARTICLE
                        </span>
                        <ShareButtons url={url} size={25} round={false} gutterClass="mr-2" />
                    </div>}
                </div>
                <div className="mt-2 blog-tags">
                    <Tags tags={displayTags ?
                            displayTags.split(",")
                                .map(tag => tag.trim())
                                .filter(tag => tag !== "")
                            : []} />
                </div>
                {description &&
                    <div className="mt-2 text-muted mt-3 description">
                        <p>{description}</p>
                    </div>
                }
            </BlogContainer>
        </div>
    );
});

export default Blog;